import React, { useEffect, useRef } from 'react';

const CertificationsPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const certifications = [
    {
      title: "BIS Certified Aluminium Blanks",
      category: "Aluminium Products",
      icon: "fas fa-circle",
      description: "Bureau of Indian Standards certification for high-quality aluminium circles and blanks, ensuring compliance with national quality standards.",
      benefits: [
        "Guaranteed quality assurance",
        "Compliance with national standards", 
        "Enhanced customer confidence",
        "Market credibility and trust"
      ],
      standards: [
        "IS 737:2018 - Aluminium and Aluminium Alloy Sheets",
        "Quality management systems",
        "Material composition standards",
        "Dimensional accuracy requirements"
      ],
      gradient: "linear-gradient(135deg, #7B3FE4, #5A1FB3)"
    },
    {
      title: "BIS Certified Aluminium Utensils",
      category: "Kitchen Products",
      icon: "fas fa-kitchen-set",
      description: "Bureau of Indian Standards certification for aluminium utensils, ensuring food safety and quality compliance for kitchen products.",
      benefits: [
        "Food safety compliance",
        "Consumer health protection",
        "Premium quality assurance",
        "Regulatory compliance"
      ],
      standards: [
        "IS 2553:2018 - Aluminium Utensils",
        "Food contact material standards",
        "Safety and hygiene requirements",
        "Durability specifications"
      ],
      gradient: "linear-gradient(135deg, #9A60FF, #7B3FE4)"
    },
    {
      title: "BIS Certified Stainless Steel Utensils", 
      category: "Steel Products",
      icon: "fas fa-hammer",
      description: "Bureau of Indian Standards certification for stainless steel utensils, guaranteeing superior quality and safety for kitchen applications.",
      benefits: [
        "Corrosion resistance assurance",
        "Food grade material certification",
        "Long-lasting durability",
        "Health and safety compliance"
      ],
      standards: [
        "IS 4592:2019 - Stainless Steel Utensils",
        "Material grade specifications",
        "Chemical composition standards", 
        "Performance requirements"
      ],
      gradient: "linear-gradient(135deg, #14B87A, #9A60FF)"
    }
  ];

  const qualityFeatures = [
    {
      icon: "fas fa-shield-alt",
      title: "Quality Assurance",
      description: "Rigorous quality control processes ensuring every product meets the highest standards"
    },
    {
      icon: "fas fa-microscope", 
      title: "Laboratory Testing",
      description: "Comprehensive testing procedures using advanced laboratory equipment and techniques"
    },
    {
      icon: "fas fa-certificate",
      title: "Compliance Standards",
      description: "Full adherence to national and international quality and safety standards"
    },
    {
      icon: "fas fa-users-cog",
      title: "Expert Team", 
      description: "Dedicated quality assurance team with extensive experience and expertise"
    }
  ];

  const documents = [
    {
      title: "ISO 9001:2015 Certificate",
      description: "Certificate of Registration confirming our Quality Management System conforms to ISO 9001:2015 for the manufacture of utensils.",
      logo: "/images/logos/ISO.png",
      file: "/documents/BB-Industry-ISO-9001-2015-Certificate.pdf"
    },
    {
      title: "BIS License",
      description: "Bureau of Indian Standards licence granting B B Industry the right to use the Standard Mark on our certified products.",
      logo: "/images/logos/BIS.png",
      file: "/documents/BB-Industry-BIS-License.pdf"
    }
  ];

  const complianceAreas = [
    {
      title: "Material Composition",
      description: "Strict adherence to specified material grades and chemical compositions",
      icon: "fas fa-atom"
    },
    {
      title: "Dimensional Accuracy",
      description: "Precise manufacturing within specified tolerance limits and dimensions", 
      icon: "fas fa-ruler-combined"
    },
    {
      title: "Surface Finish",
      description: "Superior surface quality meeting aesthetic and functional requirements",
      icon: "fas fa-magic"
    },
    {
      title: "Performance Testing",
      description: "Comprehensive testing for durability, strength, and functional performance",
      icon: "fas fa-tachometer-alt"
    },
    {
      title: "Food Safety",
      description: "Complete compliance with food contact material safety regulations",
      icon: "fas fa-utensils"
    },
    {
      title: "Environmental Standards",
      description: "Eco-friendly manufacturing processes and environmental compliance",
      icon: "fas fa-leaf"
    }
  ];

  return (
    <div className="certifications-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .certifications-page {
          font-family: 'Poppins', sans-serif;
          line-height: 1.6;
          color: #333;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* BIS Badge Section */
        .bis-section {
          background: #F6F7F9;
          padding: 60px 0;
          text-align: center;
        }

        .bis-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .bis-badge {
          display: inline-block;
          background: white;
          padding: 2rem 3rem;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .bis-logo {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #7B3FE4, #5A1FB3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .bis-logo i {
          font-size: 2rem;
          color: white;
        }

        .bis-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: #5A1FB3;
          margin-bottom: 0.5rem;
        }

        .bis-subtitle {
          font-size: 1.1rem;
          color: #4A5260;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        /* Image placeholder styles */
        .image-placeholder {
          width: 100%;
          height: 250px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px dashed #7B3FE4;
          transition: all 0.3s ease;
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        .image-placeholder:hover {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
        }

        .placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .placeholder-content i {
          font-size: 3rem;
          margin-bottom: 1rem;
          display: block;
        }

        .placeholder-text {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .placeholder-desc {
          font-size: 1rem;
          opacity: 0.8;
          max-width: 300px;
        }

        /* Certifications Section */
        .certifications-section {
          padding: 100px 0;
          background: white;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          color: #5A1FB3;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        .certifications-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          align-items: stretch;
        }

        .certification-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          transition: all 0.4s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .certification-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .cert-header {
          padding: 2rem;
          background: var(--gradient);
          color: white;
          position: relative;
          flex-shrink: 0;
          min-height: 200px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .cert-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="%23ffffff" opacity="0.1"><circle cx="20" cy="20" r="2"/><circle cx="40" cy="40" r="2"/><circle cx="60" cy="20" r="2"/><circle cx="80" cy="40" r="2"/></svg>');
          background-repeat: repeat;
        }

        .cert-title-row {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          position: relative;
          z-index: 2;
        }

        .cert-icon {
          width: 60px;
          height: 60px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.5rem;
        }

        .cert-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .cert-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin: 0;
          line-height: 1.3;
          min-height: 2.8rem;
          display: flex;
          align-items: center;
        }

        .cert-category {
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.8);
          position: relative;
          z-index: 2;
        }

        .cert-body {
          padding: 2rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .cert-description {
          font-size: 1rem;
          color: #4A5260;
          line-height: 1.6;
          margin-bottom: 2rem;
          flex-shrink: 0;
        }

        .cert-details {
          flex: 1;
        }

        .detail-section {
          margin-bottom: 1.5rem;
        }

        .detail-section h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #5A1FB3;
          margin-bottom: 0.8rem;
        }

        .detail-list {
          list-style: none;
        }

        .detail-list li {
          padding: 0.4rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.2rem;
          font-size: 0.95rem;
        }

        .detail-list li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-weight: bold;
        }

        /* Quality Features Section */
        .quality-section {
          padding: 100px 0;
          background: #F6F7F9;
        }

        .quality-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .quality-card {
          background: white;
          padding: 2.5rem;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .quality-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
        }

        .quality-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #7B3FE4, #5A1FB3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }

        .quality-icon i {
          font-size: 1.8rem;
          color: white;
        }

        .quality-card h3 {
          font-size: 1.4rem;
          font-weight: 600;
          color: #5A1FB3;
          margin-bottom: 1rem;
        }

        .quality-card p {
          color: #4A5260;
          line-height: 1.6;
        }

        /* Compliance Section */
        .compliance-section {
          padding: 100px 0;
          background: white;
        }

        .compliance-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .compliance-card {
          background: #F6F7F9;
          padding: 2rem;
          border-radius: 12px;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .compliance-card:hover {
          border-color: #7B3FE4;
          transform: translateY(-3px);
        }

        .compliance-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
        }

        .compliance-icon {
          width: 50px;
          height: 50px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          box-shadow: 0 3px 10px rgba(123, 63, 228, 0.2);
        }

        .compliance-icon i {
          font-size: 1.2rem;
          color: #7B3FE4;
        }

        .compliance-card h4 {
          font-size: 1.2rem;
          font-weight: 600;
          color: #5A1FB3;
          margin: 0;
        }

        .compliance-card p {
          color: #4A5260;
          line-height: 1.6;
        }

        /* Documents Section */
        .documents-section {
          padding: 100px 0;
          background: #F6F7F9;
        }

        .documents-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 2rem;
        }

        .document-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          padding: 2.5rem;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          transition: all 0.3s ease;
        }

        .document-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
        }

        .document-icon {
          width: 72px;
          height: 72px;
          background: white;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          border: 1px solid #EDE7FF;
        }

        .document-icon img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 8px;
        }

        .document-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
          color: #5A1FB3;
          margin-bottom: 0.75rem;
        }

        .document-card p {
          color: #4A5260;
          line-height: 1.6;
          margin-bottom: 1.5rem;
          flex: 1;
        }

        .document-download-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 12px 24px;
          background: #7B3FE4;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .document-download-btn:hover {
          background: #5A1FB3;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(123, 63, 228, 0.3);
        }

        /* CTA Section */
        .cta-section {
          padding: 80px 0;
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          text-align: center;
          color: white;
        }

        .cta-content h2 {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .cta-content p {
          font-size: 1.2rem;
          margin-bottom: 2rem;
          max-width: 600px;
          margin-left: auto;
          margin-right: auto;
          opacity: 0.9;
        }

        .cta-button {
          display: inline-block;
          padding: 15px 35px;
          background: #7B3FE4;
          color: white;
          text-decoration: none;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          box-shadow: 0 5px 20px rgba(123, 63, 228, 0.3);
          cursor: pointer;
          border: none;
        }

        .cta-button:hover {
          background: #5A1FB3;
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(90, 31, 179, 0.4);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .certifications-grid {
            grid-template-columns: 1fr;
          }

          .container {
            padding: 0 1rem;
          }

          .cert-header {
            min-height: auto;
          }

          .cert-title {
            min-height: auto;
          }
        }

        @media (max-width: 1024px) and (min-width: 769px) {
          .certifications-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      {/* Add Font Awesome CSS */}
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.7.2/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-certificate"></i>
          </div>
          <h1 className="animate-fade-in">Quality Certifications</h1>
          <p className="hero-subtitle animate-fade-in">
            BIS certified products ensuring the highest standards of quality, safety, and compliance across all our manufacturing lines
          </p>
        </div>
      </section>

      {/* BIS Badge Section */}
      <section className="bis-section">
        <div className="bis-container">
          <div className="bis-badge animate-on-scroll">
            <div className="bis-logo">
              <i className="fas fa-award"></i>
            </div>
            <h3 className="bis-title">BIS Certified Manufacturing</h3>
            <p className="bis-subtitle">Bureau of Indian Standards - Quality Assured</p>
          </div>
        </div>
      </section>

      {/* Certifications Gallery Section */}


      {/* Main Certifications Section */}
      <section className="certifications-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Certifications</h2>
            <p>Comprehensive BIS certifications across all our product lines ensuring quality and compliance</p>
          </div>
          
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card animate-on-scroll" style={{'--gradient': cert.gradient}}>
                <div className="cert-header">
                  <div className="cert-title-row">
                    <div className="cert-icon">
                      <i className={cert.icon}></i>
                    </div>
                    <h3 className="cert-title">{cert.title}</h3>
                  </div>
                  <div className="cert-category">{cert.category}</div>
                </div>
                <div className="cert-body">
                  <p className="cert-description">{cert.description}</p>
                  <div className="cert-details">
                    <div className="detail-section">
                      <h4>Benefits</h4>
                      <ul className="detail-list">
                        {cert.benefits.map((benefit, i) => (
                          <li key={i}>{benefit}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="detail-section">
                      <h4>Standards</h4>
                      <ul className="detail-list">
                        {cert.standards.map((standard, i) => (
                          <li key={i}>{standard}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Features Section */}
      <section className="quality-section">
        <div className="container">
          <div className="section-header">
            <h2>Quality Assurance</h2>
            <p>Our commitment to excellence through rigorous quality control and testing procedures</p>
          </div>
          
          <div className="quality-grid">
            {qualityFeatures.map((feature, index) => (
              <div key={index} className="quality-card animate-on-scroll">
                <div className="quality-icon">
                  <i className={feature.icon}></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Areas Section */}
      <section className="compliance-section">
        <div className="container">
          <div className="section-header">
            <h2>Compliance Areas</h2>
            <p>Comprehensive compliance across all aspects of manufacturing and quality control</p>
          </div>
          
          <div className="compliance-grid">
            {complianceAreas.map((area, index) => (
              <div key={index} className="compliance-card animate-on-scroll">
                <div className="compliance-header">
                  <div className="compliance-icon">
                    <i className={area.icon}></i>
                  </div>
                  <h4>{area.title}</h4>
                </div>
                <p>{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="documents-section">
        <div className="container">
          <div className="section-header">
            <h2>Certificate Documents</h2>
            <p>Download our official certification documents for your records and verification</p>
          </div>

          <div className="documents-grid">
            {documents.map((doc, index) => (
              <div key={index} className="document-card animate-on-scroll">
                <div className="document-icon">
                  <img src={doc.logo} alt={`${doc.title} logo`} loading="lazy" />
                </div>
                <h3>{doc.title}</h3>
                <p>{doc.description}</p>
                <a
                  href={doc.file}
                  className="document-download-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-download"></i>
                  View / Download PDF
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}

    </div>
  );
};

export default CertificationsPage;